import axios from '../utils/setupAxiosAuth';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';

export type HeroAffinityMatrixCell = {
  customer_index: number;
  product_index: number;
  customer_id: number;
  product_id: number;
  score: number;
  display_metric: number | null;
  has_pair: boolean;
  sales_component: number | null;
  inquiry_component: number | null;
  watchlist_component: number | null;
};

export type HeroAffinityResponse = {
  summary: {
    scored_pairs: number;
    customers_with_hero: number;
    distinct_products: number;
    scores_computed_at_max: string | null;
  };
  matrix?: {
    customers: Array<{ customer_id: number; customer_name: string }>;
    products: Array<{ product_id: number; product_name: string }>;
    cells: HeroAffinityMatrixCell[];
    cell_metric: 'score' | 'score_pct_of_row';
    matrix_size: number;
    customer_rank_by: string;
  };
  hero_leaderboard?: Array<{
    product_id: number;
    product_name: string;
    customer_count: number;
  }>;
  customer_products?: Array<{
    product_id: number;
    product_name: string;
    score: number;
    sales_component: number | null;
    inquiry_component: number | null;
    watchlist_component: number | null;
  }>;
  product_buyers?: Array<{
    customer_id: number;
    company_name: string | null;
    customer_name: string | null;
    score: number;
  }>;
  source_freshness: { scores_computed_at_max: string | null };
  last_updated_at?: string | null;
  product_landscape?: ProductLandscapePayload;
  customer_landscape?: CustomerLandscapePayload;
};

export type ProductHeatTier = 'hot' | 'medium' | 'low';

export type ProductLandscapeRow = {
  product_id: number;
  product_name: string;
  buyer_count: number;
  sum_score: number;
  avg_score: number;
  max_score: number;
  tier: ProductHeatTier;
};

export type ProductLandscapePayload = {
  tier_rules: { method: string; description: string };
  tier_summary: { hot: number; medium: number; low: number };
  products: ProductLandscapeRow[];
  chart_products: ProductLandscapeRow[];
  score_bucket_heatmap: {
    bucket_labels: string[];
    products: Array<{ product_id: number; product_name: string }>;
    cells: Array<{ product_index: number; bucket_index: number; customer_count: number }>;
  };
};

export type CustomerLandscapeRow = {
  customer_id: number;
  customer_name: string;
  product_count: number;
  sum_score: number;
  avg_score: number;
  max_score: number;
  tier: ProductHeatTier;
};

export type CustomerLandscapePayload = {
  tier_rules: { method: string; description: string };
  tier_summary: { hot: number; medium: number; low: number };
  max_product_count: number;
  breadth_histogram: Array<{ product_count: number; customer_count: number }>;
  customers: CustomerLandscapeRow[];
  chart_customers: CustomerLandscapeRow[];
  score_bucket_heatmap: {
    bucket_labels: string[];
    customers: Array<{ customer_id: number; customer_name: string }>;
    cells: Array<{ customer_index: number; bucket_index: number; product_count: number }>;
  };
};

export const fetchHeroProductAffinity = async (params: {
  view?: string;
  matrix_size?: number;
  customer_rank_by?: string;
  cell_metric?: string;
  customer_id?: string;
  product_id?: string;
}): Promise<HeroAffinityResponse> => {
  const response = await axios.get(
    `${getAdminApiBaseUrl()}/customers/hero-product-affinity`,
    { params }
  );
  return response.data;
};

export const getBuyerAppBaseUrl = (): string => {
  const mainSite = (process.env.REACT_APP_MAIN_WEBSITE_URL || "").trim();
  if (mainSite && mainSite !== "undefined") {
    return mainSite.replace(/\/$/, "");
  }
  const configured = (process.env.REACT_APP_BUYER_APP_URL || "").trim();
  if (configured && configured !== "undefined") {
    return configured.replace(/\/$/, "");
  }
  return "https://credcosourcing.com";
};

export const heroBestPriceUrl = (customerId: number, productId: number): string => (
  `${getBuyerAppBaseUrl()}/best-price?c=${customerId}&p=${productId}`
);
